import { useCarbarStore } from '@/stores/carbar'

// Approximate heights used before the first client measurement to avoid layout shift.
// Expanded fallback is sized for narrow mobile Chrome where the copy wraps tallest.
const FALLBACK_EXPANDED_HEIGHT = 300
const FALLBACK_COLLAPSED_HEIGHT = 48
const MAIN_HEADER_HEIGHT = 80 // matches h-20 on .main-header

export const useSecurityNotice = () => {
	const carbar = useCarbarStore()
	const isListingOnly = useIsListingOnly()

	// Shown only in listing-only mode (NUXT_PUBLIC_LISTING_ONLY), when certain
	// site functions are intentionally unavailable.
	const showSecurityNotice = computed(() => isListingOnly)

	const isSecurityNoticeCollapsed = computed(
		() => carbar.header.isSecurityNoticeCollapsed
	)

	const securityNoticeHeight = computed(() => {
		const measured = carbar.header.securityNoticeHeight
		if (measured > 0) return measured
		return isSecurityNoticeCollapsed.value
			? FALLBACK_COLLAPSED_HEIGHT
			: FALLBACK_EXPANDED_HEIGHT
	})

	const setSecurityNoticeHeight = (height) => {
		if (height > 0) {
			carbar.header.securityNoticeHeight = height
		}
	}

	// Clear measured height so the matching fallback applies until remeasure —
	// otherwise a stale collapsed height leaves the expanded notice covering the header.
	const clearMeasuredHeight = () => {
		carbar.header.securityNoticeHeight = 0
	}

	const setSecurityNoticeCollapsed = (collapsed) => {
		clearMeasuredHeight()
		carbar.header.isSecurityNoticeCollapsed = collapsed
	}

	const toggleSecurityNoticeCollapsed = () => {
		clearMeasuredHeight()
		carbar.header.isSecurityNoticeCollapsed =
			!carbar.header.isSecurityNoticeCollapsed
	}

	const securityNoticeMarginStyle = computed(() =>
		showSecurityNotice.value
			? { marginTop: `${securityNoticeHeight.value}px` }
			: undefined
	)

	// Spacer clears the fixed notice plus the main header.
	const securityNoticeSpacerStyle = computed(() =>
		showSecurityNotice.value
			? {
					height: `${securityNoticeHeight.value + MAIN_HEADER_HEIGHT}px`,
				}
			: undefined
	)

	return {
		showSecurityNotice,
		securityNoticeHeight,
		isSecurityNoticeCollapsed,
		setSecurityNoticeHeight,
		setSecurityNoticeCollapsed,
		toggleSecurityNoticeCollapsed,
		securityNoticeMarginStyle,
		securityNoticeSpacerStyle,
	}
}
